import React, { useState, useEffect } from 'react';
import { CallApi } from "../callApi";
import { APIS } from "../../../_config";
import toast from 'react-hot-toast';

// Global upload state persistence key
const UPLOAD_STORAGE_KEY = 'wine-upload-states';
// NOTE: Start time & simulation keys removed now that timeout simulation is gone.
// When integrating real endpoint just update uploadFileInBackground.

// Initialize from localStorage if available
let globalUploadStates: { [key: string]: boolean } = (() => {
  try {
    const stored = localStorage.getItem(UPLOAD_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
})();

let uploadStateListeners: Array<(states: { [key: string]: boolean }) => void> = [];
let uploadCompleteListeners: Array<() => void> = [];
// One-time flag to perform aborted upload cleanup after a full page reload.
let abortedCleanupDone = false;

const saveUploadStatesToStorage = () => {
    try { localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(globalUploadStates)); } catch { /* silent */ }
};

const notifyUploadStateChange = () => {
    saveUploadStatesToStorage(); // Persist to localStorage
    uploadStateListeners.forEach(listener => listener(globalUploadStates));
};

const notifyUploadComplete = () => {
  uploadCompleteListeners.forEach(listener => listener());
};

// Background upload function (replace placeholder with real API call).
// IMPLEMENTATION GUIDE:
// 1. Replace 'APIS.UPLOAD.UPLOAD' with actual endpoint constant.
// 2. If the backend returns success immediately, keep as-is.
// 3. If backend queues processing, extend with status polling before calling finalize.
const uploadFileInBackground = async (formData: FormData, fileType: string) => {
    try {
        // TODO: Uncomment and set correct endpoint when available:
        // await CallApi.POST(APIS.UPLOAD.UPLOAD, formData, true, 'multipart/form-data');
        // Simulated immediate success until endpoint is wired:
        // Remove the next line once real API is active.
        await Promise.resolve();

        // Success cleanup
        delete globalUploadStates[fileType];
        notifyUploadStateChange();
        notifyUploadComplete();
        toast.success(`${fileType} uploaded successfully!`);
    } catch (e) {
        delete globalUploadStates[fileType];
        notifyUploadStateChange();
        toast.error(`Failed to upload ${fileType}`);
    }
};

export function useUploadApi() {
    const [localUploadStates, setLocalUploadStates] = useState<{ [key: string]: boolean }>(globalUploadStates);

    // Subscribe to global upload state changes
    useEffect(() => {
        const unsubscribe = subscribeToUploadStates((states) => {
            setLocalUploadStates(states);
        });
        return unsubscribe;
    }, []);

        // On first mount after a full browser reload, any "true" flags mean the prior uploads were interrupted.
        // Clear them and inform the user so the UI isn't stuck.
        useEffect(() => {
            if (abortedCleanupDone) return;
            const pending = Object.keys(globalUploadStates).filter(k => globalUploadStates[k]);
            if (pending.length > 0) {
                // Clear all lingering uploading states
                globalUploadStates = {};
                notifyUploadStateChange();
                // Show a toast per interrupted file_type (could be batched if needed)
                pending.forEach(ft => {
                    try { toast.error(`${ft} upload was interrupted. Please upload again.`); } catch { /* silent */ }
                });
            }
            abortedCleanupDone = true;
        }, []);

    // (Simulation interval removed; real-time updates come from direct promise resolution.)

    const subscribeToUploadStates = (callback: (states: { [key: string]: boolean }) => void) => {
        uploadStateListeners.push(callback);
        return () => {
            uploadStateListeners = uploadStateListeners.filter(listener => listener !== callback);
        };
    };

    const subscribeToUploadComplete = (callback: () => void) => {
        uploadCompleteListeners.push(callback);
        return () => {
            uploadCompleteListeners = uploadCompleteListeners.filter(listener => listener !== callback);
        };
    };

    //get upload list
    const getList = async (params: any = {}): Promise<any> => {
        return CallApi.GET(APIS.DOWNLOAD.LIST, {...params}, true);
    };

    //upload file
    const uploadFile = (formData: FormData): void => {
        const fileType = formData.get('file_type') as string;
        
        // Set uploading state globally
        globalUploadStates[fileType] = true;
        notifyUploadStateChange();
        // Start upload process in background (fire and forget)
        uploadFileInBackground(formData, fileType).catch(() => {
            // Ensure state is cleaned up even if background function fails
            delete globalUploadStates[fileType];
            notifyUploadStateChange();
        });
    };

    const isUploading = (fileType: string): boolean => {
        // Force reload from localStorage to check persistence
                try {
                    const stored = localStorage.getItem(UPLOAD_STORAGE_KEY);
                    const storedStates = stored ? JSON.parse(stored) : {};
                    if (JSON.stringify(storedStates) !== JSON.stringify(globalUploadStates)) {
                        globalUploadStates = storedStates;
                        setLocalUploadStates(storedStates);
                    }
                } catch {/* silent */}
        return !!globalUploadStates[fileType];
    };

    // Manual cleanup function for stuck states
    // (Clear helpers removed; can be reintroduced if needed.)

    return{
        getList,
        uploadFile,
        isUploading,
        uploadStates: localUploadStates,
        subscribeToUploadComplete
    };
    
}